import { useMemo, useState, useCallback } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "react-toastify"
import { Col, Row, UncontrolledTooltip, Button, Badge } from "reactstrap"

import TableContainer from "components/Common/TableContainer"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import PageWrapper from "components/Common/PageWrapper"
import { CanView } from "components/Common/CanView"
import { ACCESS } from "constants/accessPermissions"
import { useListParams } from "hooks/useListParams"
import { usePettyCashPaymentFroms, useDeletePettyCashPaymentFrom, usePettyCashBalance } from "hooks/usePettyCashMaster"
import { useFilteredVehicleOptions } from "hooks/stockFilterHook"
import { formatCurrency } from "utils/formatCurrency"

import PettyCashPaymentFromAddEdit from "./PettyCashPaymentFromAddEdit"
import PettyCashTransactionAddEdit from "../PettyCashTransactionList/PettyCashTransactionAddEdit"

const PettyCashPaymentFromList = () => {
  document.title = "Petty Cash Payment Sources"

  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [selectedData, setSelectedData] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)
  const [topUpModal, setTopUpModal] = useState(false)
  const [topUpSource, setTopUpSource] = useState(null)

  const { params, setPage, setSearch, resetParams, hasActiveFilters } = useListParams({ page: 1, search: "" })
  const queryParams = useMemo(() => ({ page: params.page || 1, per_page: 10, search: params.search }), [params])

  const queryClient = useQueryClient()
  const { data: responseData, isLoading, refetch, isRefetching } = usePettyCashPaymentFroms(queryParams)
  const { mutateAsync: deleteItem, isPending: deleteLoading } = useDeletePettyCashPaymentFrom()
  const { data: balanceResponse } = usePettyCashBalance()
  const balance = balanceResponse?.data?.balance ?? 0

  const { isLoading: optionLoading, raw: rawData } = useFilteredVehicleOptions()
  const paymentModesData = useMemo(() => rawData?.petty_cash?.payment_modes || [], [rawData])
  const paymentForsData = useMemo(() => rawData?.petty_cash?.payment_fors || [], [rawData])
  const paymentFromsData = useMemo(() => rawData?.petty_cash?.payment_froms || [], [rawData])
  const employeesData = useMemo(() => rawData?.employees || [], [rawData])

  const toggleModal = useCallback(() => {
    setModal(prev => !prev)
    if (modal) { setSelectedData(null); setIsEdit(false) }
  }, [modal])

  const handleAddNewClick = () => { setSelectedData(null); setIsEdit(false); setModal(true) }
  const handleEditClick = item => { setSelectedData(item); setIsEdit(true); setModal(true) }
  const handleDeleteClick = item => { setSelectedData(item); setDeleteModal(true) }

  const topUpTransaction = useMemo(
    () => ({
      payment_type: "cashin",
      paymentFrom: topUpSource ? { id: topUpSource.id, name: topUpSource.name } : undefined,
    }),
    [topUpSource]
  )

  const handleTopUpClick = item => { setTopUpSource(item); setTopUpModal(true) }
  const toggleTopUpModal = () => {
    setTopUpModal(prev => !prev)
    if (topUpModal) setTopUpSource(null)
  }
  const handleTopUpSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["petty-cash-payment-from"] })
    queryClient.invalidateQueries({ queryKey: ["petty-cash-balance"] })
  }

  const handleDelete = async () => {
    if (!selectedData?.id) return
    try {
      const res = await deleteItem(selectedData.id)
      toast.success(res?.message ?? "Payment source deleted successfully.")
      setDeleteModal(false)
      refetch()
    } catch {
      toast.error("Failed to delete payment source.")
    }
  }

  const columns = useMemo(() => [
    { header: "ID", accessorKey: "id" },
    { header: "Name", accessorKey: "name" },
    { header: "Slug", accessorKey: "slug" },
    { header: "Description", cell: ({ row }) => row.original.description || "-" },
    {
      header: "Balance",
      cell: ({ row }) => {
        const sourceBalance = row.original.balance ?? 0
        return (
          <Badge color={sourceBalance < 0 ? "danger" : "success"}>
            {formatCurrency(sourceBalance, "BDT")}
          </Badge>
        )
      },
    },
    {
      header: "Action",
      cell: ({ row }) => (
        <ul className="list-unstyled hstack gap-1 mb-0">
          <li>
            <Button color="success" size="sm" onClick={() => handleTopUpClick(row.original)} id={`topup-pcpf-${row.original.id}`}>
              <i className="mdi mdi-cash-plus" />
            </Button>
            <UncontrolledTooltip placement="top" target={`topup-pcpf-${row.original.id}`}>Top Up</UncontrolledTooltip>
          </li>
          <CanView access={[ACCESS.PETTY_CASH_PAYMENT_FROM.EDIT]}>
            <li>
              <Button color="info" size="sm" onClick={() => handleEditClick(row.original)} id={`edit-pcpf-${row.original.id}`}>
                <i className="mdi mdi-pencil-outline" />
              </Button>
              <UncontrolledTooltip placement="top" target={`edit-pcpf-${row.original.id}`}>Edit</UncontrolledTooltip>
            </li>
          </CanView>
          <CanView access={[ACCESS.PETTY_CASH_PAYMENT_FROM.DELETE]}>
            <li>
              <Button color="danger" size="sm" onClick={() => handleDeleteClick(row.original)} id={`delete-pcpf-${row.original.id}`}>
                <i className="mdi mdi-delete-outline" />
              </Button>
              <UncontrolledTooltip placement="top" target={`delete-pcpf-${row.original.id}`}>Delete</UncontrolledTooltip>
            </li>
          </CanView>
        </ul>
      ),
    },
  ], [])

  const data = responseData?.data?.data || []
  const paginationInfo = responseData?.data

  return (
    <PageWrapper title="Petty Cash" breadcrumbItem="Payment Sources">
      <div className="d-flex align-items-center mb-3">
        <h5 className="mb-0 mt-3 card-title flex-grow-1">
          Total <Badge className="badge-soft-info">{paginationInfo?.total ?? 0}</Badge>{" "}
          Petty Cash Balance{" "}
          <Badge color={balance < 0 ? "danger" : "success"}>{formatCurrency(balance, "BDT")}</Badge>
        </h5>
        <div className="flex-shrink-0">
          <CanView access={[ACCESS.PETTY_CASH_PAYMENT_FROM.ADD]}>
            <Button color="primary" onClick={handleAddNewClick}>Add New</Button>
          </CanView>
          <Button color="light" onClick={refetch} className="ms-1">
            <i className={`mdi mdi-refresh ${isRefetching ? "mdi-spin" : ""}`} />
          </Button>
          {hasActiveFilters && <Button color="danger" onClick={resetParams} className="ms-1">Reset Filters</Button>}
        </div>
      </div>

      <DeleteModal show={deleteModal} onDeleteClick={handleDelete} loading={deleteLoading} onCloseClick={() => setDeleteModal(false)} />

      <Row className="mb-3">
        <Col sm={4}><GlobalInputSearch value={params.search} onChange={setSearch} /></Col>
      </Row>

      <TableContainer
        columns={columns}
        data={data}
        isPagination={true}
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isRefetching}
      />

      <PettyCashPaymentFromAddEdit modal={modal} isEdit={isEdit} editedData={selectedData} toggleModal={toggleModal} />

      <PettyCashTransactionAddEdit
        modal={topUpModal}
        toggleModal={toggleTopUpModal}
        isEdit={false}
        transaction={topUpTransaction}
        optionLoading={optionLoading}
        paymentModesData={paymentModesData}
        paymentForsData={paymentForsData}
        paymentFromsData={paymentFromsData}
        receiversData={employeesData}
        onSuccess={handleTopUpSuccess}
      />
    </PageWrapper>
  )
}

export default PettyCashPaymentFromList
